// Red, skeniranje, pauza i potvrda isporuke za "Isporuka narudžbi v4" (pages/canteen/v4.vue).
//
// Pravilo reda je isto kao na staroj stranici: na ekranu je posljednjih N skeniranja (N = settings.colInRow),
// kartice stoje redom kako su skenirane (najnovija lijevo), a kome se služi je najstarija (u boji).
// Kad stigne N+1, najstarija ispada. Novo je: ona ne nestaje nego ide u traku "Izdato" (odakle se
// vraća u red), a "Izdato →" (ili tipka →) pomjera red prije nego što stigne novo skeniranje.
//
// Očekuje u komponenti: settings, company (aktivna firma), online, say(), playForTicket(), tx().
import paymentService from "@/services/payment";
import {
  KINDS,
  normalizeOrderGroup,
  parseScanned,
  classifyRequestError,
  hhmm,
  todayString,
} from "@/utils/canteenOutcome";
import { paymentText } from "@/utils/canteenPayment";

export const DUP_MS = 10000; // isti ID u ovom roku osvježava postojeću karticu, bez novog zahtjeva
export const REQUEST_TIMEOUT = 8000; // bez odgovora = "Nema veze sa serverom"
const ERROR_PAUSE = 2; // sekunde: poslije greške veze skener se vraća brzo
const MAX_PAST = 6; // koliko izdatih kartica ostaje u traci

const emptyOutcome = (kind) => ({
  kind,
  success: false,
  text: "",
  knownReason: false,
  sound: "",
  user: null,
  shift: null,
  items: [],
  takenAt: "",
  info: [],
  pay: null,
  takeParams: null,
});

export default {
  data() {
    return {
      recent: [], // sve kartice, najnovija prva (aktivne i izdate)
      seq: 0, // brojač kartica
      reqSeq: 0, // redni broj zahtjeva (zastarjeli odgovor se odbacuje)
      busy: false, // provjera u toku
      cool: null, // automatska pauza: { id, total, end, left }
      coolId: 0,
      waitNext: false, // ručni režim: čeka se "Sledeći (+)"
      sessionExpired: false,
      clearAt: 0, // kad se ekran čisti
      clearLeft: 0,
      scanInput: "",
      inputBad: "",
      orderDate: todayString(),
      announce: "",
    };
  },
  computed: {
    keep() {
      return Math.max(1, Number(this.settings.colInRow) || 3);
    },
    // kartice na ekranu, redom kojim stoje (najnovija prva)
    active() {
      return this.recent.filter((t) => !t.done);
    },
    visible() {
      return this.active.slice(0, this.keep);
    },
    // redoslijed usluživanja: najstariji prvi (ili posljednji skenirani prvi)
    queue() {
      const a = this.visible;
      return this.settings.order === "newest" ? a : a.slice().reverse();
    },
    head() {
      return this.queue[0] || null;
    },
    positions() {
      const m = {};
      this.queue.forEach((t, i) => {
        m[t.id] = i + 1;
      });
      return m;
    },
    past() {
      return this.recent
        .filter((t) => t.done)
        .sort((a, b) => b.doneTs - a.doneTs);
    },
    ready() {
      return !this.busy && !this.cool && !this.waitNext && !this.sessionExpired && this.online;
    },
    confirmAuto() {
      return !!this.companySettings.confirm_delivery;
    },
    signal() {
      const s = this.cool;
      if (!this.online) return { s: "sys", icon: "mdi-wifi-off", title: this.tx("sig.offline"), sub: this.tx("sig.offline_sub") };
      if (this.sessionExpired) return { s: "sys", icon: "mdi-lock-clock", title: this.tx("sig.session"), sub: this.tx("sig.session_sub") };
      if (this.busy) return { s: "busy", icon: "mdi-cached", spin: true, title: this.tx("sig.busy"), sub: this.tx("sig.busy_sub") };
      if (s) {
        if (this.settings.typeTraffic === "three" && s.left <= 3) {
          return { s: "amber", num: s.left, total: 3, title: this.tx("sig.prep"), sub: this.tx("sig.wait_sub") };
        }
        return { s: "red", num: s.left, total: s.total, title: `${this.tx("sig.wait")} ${s.left} s`, sub: this.tx("sig.wait_sub") };
      }
      if (this.waitNext) return { s: "red", icon: "mdi-plus", title: this.tx("sig.manual"), sub: this.tx("sig.manual_sub"), next: true };
      return { s: "green", icon: "mdi-check-bold", title: this.tx("sig.ready"), sub: this.tx("sig.ready_sub") };
    },
    signalColor() {
      return { green: "var(--a-green)", red: "var(--a-red)", amber: "var(--a-yellow)", busy: "#c9ced7", sys: "var(--a-ink)" }[this.signal.s];
    },
  },
  methods: {
    clearSeconds() {
      return Math.max(10, Number(this.settings.clearPreviousOrdersTime) || 120);
    },
    touchClear() {
      this.clearAt = Date.now() + this.clearSeconds() * 1000;
    },
    newTicket(kind, outcome, code, extra = {}) {
      const now = new Date();
      return {
        id: ++this.seq,
        ts: now.getTime(),
        at: hhmm(now),
        code: String(code || ""),
        kind,
        outcome,
        conf: null, // null | pending | sending | done | failed
        confAt: "",
        how: "", // auto | manual
        done: false,
        doneHow: "", // manual | auto
        doneAt: "",
        doneTs: 0,
        err: null,
        ...extra,
      };
    },

    // ---------- skeniranje ----------
    // Vraća true ako je provjera pokrenuta.
    async scan(code, opts = {}) {
      const { replaceId = null, source = "manual", force = false } = opts;
      code = String(code);
      if (force) {
        // izričit "Pokušaj ponovo": pauza se prekida, ali se ne ide preko provjere u toku
        if (this.busy || !this.online) return false;
        this.cool = null;
        this.waitNext = false;
        this.sessionExpired = false;
      } else if (!this.ready) {
        if (source === "manual") this.say(this.tx("toast.wait"));
        return false;
      }
      if (!replaceId && this.isDuplicate(code)) return false;
      if (this.settings.takeCurrentDate) this.orderDate = todayString();

      this.busy = true;
      const seq = ++this.reqSeq;
      let ticket;
      try {
        const res = await paymentService.getOrderGroupByUserTesting(
          code,
          { date: this.orderDate, lang: this.settings.language },
          { timeout: REQUEST_TIMEOUT }
        );
        if (seq !== this.reqSeq) return false;
        ticket = this.ticketFromResponse(res, code);
      } catch (err) {
        if (seq !== this.reqSeq) return false;
        ticket = this.ticketFromError(err, code);
      }
      this.busy = false;
      this.accept(ticket, replaceId);
      return true;
    },
    ticketFromResponse(res, code) {
      const data = res && res.data && res.data.data;
      if (!data || typeof data !== "object") {
        console.error("[canteen-v4] neočekivan odgovor", res && res.data);
        return this.newTicket("error", emptyOutcome("error"), code, { err: { kind: "error", status: 0, message: "unexpected response" } });
      }
      const outcome = normalizeOrderGroup(data, {
        code,
        restaurantId: this.company && this.company.restaurant && this.company.restaurant.id,
        companyId: this.company && this.company.id,
        date: this.orderDate,
      });
      return this.newTicket(outcome.kind, outcome, code);
    },
    ticketFromError(err, code) {
      const c = classifyRequestError(err);
      console.error("[canteen-v4] provjera nije uspjela", c, err);
      return this.newTicket(c.kind, emptyOutcome(c.kind), code, { err: c });
    },
    // isti ID u 10 s: kartica se osvježi, ne šalje se novi zahtjev ni nova potvrda
    isDuplicate(code) {
      const last = this.recent[0];
      const now = Date.now();
      if (!last || last.done || last.code !== code || last.kind === "error" || last.kind === "session") return false;
      if (now - last.ts >= DUP_MS) return false;
      last.ts = now;
      if (now - (this._dupToastAt || 0) > 3000) {
        this._dupToastAt = now;
        this.say(this.tx("toast.same"));
      }
      return true;
    },
    accept(ticket, replaceId) {
      const old = replaceId && this.recent.find((t) => t.id === replaceId);
      if (old) {
        ticket.id = old.id;
        this.recent.splice(this.recent.indexOf(old), 1, ticket);
      } else {
        this.recent.unshift(ticket);
        this.trim();
      }
      this.touchClear();
      const who = ticket.outcome.user ? ", " + this.toLatin(ticket.outcome.user.name) + " " + this.toLatin(ticket.outcome.user.lastname) : "";
      const pay = ticket.outcome.pay ? `, ${this.tx("pay.collect")} ${paymentText(ticket.outcome.pay, this.$i18n.locale)}` : "";
      this.announce = `${this.tx("o." + ticket.kind)}${who}${pay}`;
      this.playForTicket(ticket);
      if (KINDS[ticket.kind].can) {
        if (this.confirmAuto) this.confirmTicket(ticket, "auto");
        else ticket.conf = "pending";
      }
      if (ticket.kind === "session") this.sessionExpired = true;
      this.startPause(ticket);
    },

    // ---------- pauza ----------
    startPause(ticket) {
      if (ticket.kind === "session") return;
      if (ticket.kind === "error") {
        if (this.settings.mode === "auto") this.beginCool(ERROR_PAUSE);
        return;
      }
      if (this.settings.mode === "auto") {
        this.beginCool(Math.max(1, parseInt(this.settings.autoDuration, 10) || 6));
      } else {
        this.waitNext = true;
      }
    },
    beginCool(sec) {
      this.cool = { id: ++this.coolId, total: sec, end: Date.now() + sec * 1000, left: sec };
    },
    next() {
      if (this.waitNext) this.waitNext = false;
    },

    // ---------- potvrda isporuke ----------
    // how: auto (potvrda pri skeniranju, kad je tako podešeno za firmu) ili manual (dugme)
    async confirmTicket(tk, how) {
      if (!tk || !tk.outcome.takeParams || tk.conf === "sending" || tk.conf === "done") return;
      tk.conf = "sending";
      try {
        await paymentService.processOrderTakeOver(tk.outcome.takeParams);
        tk.conf = "done";
        tk.confAt = hhmm(new Date());
        tk.how = how;
        // ručna potvrda je ujedno "Izdato": kartica ide u traku, sljedeći postaje glavni
        if (how === "manual" && this.keep > 1 && !tk.done) this.markDone(tk, "manual", true);
      } catch (err) {
        console.error("[canteen-v4] potvrda isporuke nije uspjela", err);
        tk.conf = "failed";
      }
    },
    onConfirm(id) {
      const tk = this.recent.find((t) => t.id === id);
      if (tk) this.confirmTicket(tk, this.confirmAuto ? "auto" : "manual");
    },

    // ---------- red ----------
    markDone(tk, how, toast) {
      tk.done = true;
      tk.doneHow = how;
      tk.doneAt = hhmm(new Date());
      tk.doneTs = Date.now();
      this.touchClear();
      this.trimPast();
      if (toast) {
        const u = tk.outcome.user;
        this.say(`${this.tx("toast.done")}${u ? " · " + this.toLatin(u.name) + " " + this.toLatin(u.lastname) : ""}`);
      }
      this.$nextTick(() => this.focusHead());
    },
    // glavna kartica je izdata: ide u "Izdato", sljedeći u redu postaje glavni
    advance() {
      const tk = this.head;
      if (!tk || this.keep <= 1 || tk.kind === "session") return;
      if (KINDS[tk.kind].can && tk.conf !== "done") return;
      this.markDone(tk, "manual");
    },
    restore(id) {
      const tk = this.recent.find((t) => t.id === id);
      if (!tk || !tk.done) return;
      if (this.active.length >= this.keep) {
        this.say(this.tx("toast.full"));
        return;
      }
      tk.done = false;
      tk.doneHow = "";
      tk.doneAt = "";
      tk.doneTs = 0;
      this.touchClear();
      this.closeSheet();
    },
    // više od N aktivnih: najstarija ide u "Izdato" (kao danas, kad kartica ispadne), ništa ne nestaje bez traga
    trim() {
      const now = Date.now();
      this.active.slice(this.keep).forEach((t) => {
        t.done = true;
        t.doneHow = "auto";
        t.doneAt = hhmm(new Date(now));
        t.doneTs = now;
      });
      this.trimPast();
    },
    trimPast() {
      const done = this.past;
      if (done.length <= MAX_PAST) return;
      const drop = new Set(done.slice(MAX_PAST).map((t) => t.id));
      this.recent = this.recent.filter((t) => !drop.has(t.id));
    },
    clearAll() {
      this.recent = [];
      this.clearAt = 0;
    },
    retryTicket(id) {
      const tk = this.recent.find((t) => t.id === id);
      if (tk && tk.code) this.scan(tk.code, { replaceId: id, source: "retry", force: true });
    },

    // ---------- ID polje ----------
    onIdInput(value) {
      this.scanInput = value;
      this.inputBad = /^\d*$/.test(value) ? "" : this.tx("id.bad");
    },
    submitId() {
      const v = this.scanInput.trim();
      if (!v) return;
      if (!/^\d+$/.test(v)) {
        this.inputBad = this.tx("id.bad");
        return;
      }
      if (!this.ready) {
        this.say(this.tx("toast.wait"));
        return;
      }
      this.inputBad = "";
      this.scanInput = "";
      this.scan(v, { source: "manual" });
    },
    // kamera ili čitač: sadržaj kartice ({"id":1001,"secure":"…"}) ili samo broj
    onScanned(raw, source) {
      const p = parseScanned(raw);
      if (!p.ok) {
        const now = Date.now();
        if (now - (this._badToastAt || 0) > 3000) {
          this._badToastAt = now;
          this.say(this.tx("toast.notcard"));
        }
        return;
      }
      this.scan(p.id, { source });
    },

    // ---------- sat ----------
    tick() {
      const now = Date.now();
      if (this.cool) {
        const left = Math.max(0, Math.ceil((this.cool.end - now) / 1000));
        if (left !== this.cool.left) this.cool.left = left;
        if (now >= this.cool.end) this.cool = null;
      }
      if (this.clearAt) {
        if (now >= this.clearAt) {
          this.clearAll();
        } else {
          const l = Math.ceil((this.clearAt - now) / 1000);
          if (l !== this.clearLeft) this.clearLeft = l;
        }
      }
      // ponoć: datum provjere prelazi na novi dan (ako je uključen "Trenutni datum")
      if (this.settings.takeCurrentDate) {
        const today = todayString();
        if (today !== this.orderDate) this.orderDate = today;
      }
    },
    resetPause() {
      this.cool = null;
      this.waitNext = false;
    },
    focusHead() {
      const el = this.$el && this.$el.querySelector && this.$el.querySelector(".c4-card.head [data-fk='done'], .c4-card.head [data-fk='confirm']");
      if (el) el.focus({ preventScroll: true });
    },
  },
};
